/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Icon from 'carbon-react/lib/components/icon';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { DataField } from '../../../types';
import { DataOption } from './data-option';
import { flattenFinFields, getFlattenedFormulas, isChild } from '../../../utils-data';
import { ReportingSidebar } from '../../reporting-sidebar/reporting-sidebar';
import { useReportBuilderContext } from '../../../report-builder.context';
import { useRowFormulas } from '../../../hooks/use-row-formulas/use-row-formulas';

import * as styles from './data-selector.scss';

type DataType = 'finData' | 'drivers' | 'formulas';
interface DisplayedData {
  option: DataField;
  key: string;
  overrideName?: string;
  description?: string;
  isParent?: boolean;
}

interface Props {
  isOpenDialog?: boolean;
  handleCloseDialog: () => void;
  openFormulaDialog: (id?: string) => void;
  isSecondary?: boolean;
}
export const SelectDataModal = ({ isOpenDialog, handleCloseDialog, openFormulaDialog, isSecondary = false }: Props) => {
  const intl = useIntl();
  const {
    allFinFields,
    allFinFieldsArray,
    getSelectedFinFields,
    setSelectedFinFields,
    getFinFieldsOptions,
    rowFormulaData,
    futrliFormulaData,
    isTemplate,
    isWorkspaceTemplate
  } = useReportBuilderContext();
  const { getTransformedRowFormulas } = useRowFormulas();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;
  const selectedFinFields = getSelectedFinFields();
  const [searchTxt, setSearchTxt] = useState('');
  const [rowFormulas, setRowFormulas] = useState<DataField[]>([]);
  const [rowFormulasArray, setRowFormulasArray] = useState<DataField[]>([]);
  const [typeExpanded, setTypeExpanded] = useState({ finData: false, drivers: false, formulas: false });
  const [typeExpandedAll, setTypeExpandedAll] = useState({ finData: false, drivers: false, formulas: false });
  const [selectedOptions, setSelectedOptions] = useState(selectedFinFields);
  const [excludedOptions, setExcludedOptions] = useState<Record<string, string[]>>({});
  const [includedOptions, setIncludedOptions] = useState<Record<string, string[]>>({});
  const [displayedData, setDisplayedData] = useState<{
    finData: DisplayedData[];
    drivers: DisplayedData[];
    formulas: DisplayedData[];
  }>({ finData: [], drivers: [], formulas: [] });
  const [highlightedRef, setHighlightedRef] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [updateSelectedOptionsPending, setUpdateSelectedOptionsPending] = useState(false);

  const plIndex = useMemo(() => allFinFields.findIndex(e => e.ref === 'profit_and_loss'), [allFinFields]);
  const bsIndex = useMemo(() => allFinFields.findIndex(e => e.ref === 'balance_sheet'), [allFinFields]);
  const cfIndex = useMemo(() => allFinFields.findIndex(e => e.ref === 'cash_flow'), [allFinFields]);
  const drIndex = useMemo(() => allFinFields.findIndex(e => e.ref === 'drivers'), [allFinFields]);

  useEffect(() => {
    const options = getFinFieldsOptions();
    const excluded: Record<string, string[]> = {};
    const included: Record<string, string[]> = {};
    options.forEach(o => {
      excluded[o.ref] = o.options?.excludedChildren || [];
      included[o.ref] = o.options?.includedChildren || [];
    });
    setExcludedOptions(excluded);
    setIncludedOptions(included);
  }, [getFinFieldsOptions]);

  useEffect(() => {
    setUpdateSelectedOptionsPending(true);
  }, [rowFormulaData]);

  useEffect(() => {
    if (updateSelectedOptionsPending) {
      setSelectedOptions(
        selectedOptions
          .filter(
            o =>
              o.classifier !== 'row-formula' ||
              futrliFormulaData?.find(f => f.ref === o.ref) ||
              rowFormulaData?.find(f => f.ref === o.ref)
          )
          .map(o => {
            const foundField = rowFormulaData?.find(f => f.ref === o.ref);
            return { ...o, name: foundField?.name || o.name };
          })
      );
      setUpdateSelectedOptionsPending(false);
    }
  }, [updateSelectedOptionsPending, selectedOptions, rowFormulaData, futrliFormulaData]);

  const filteredSelectedOptions = useMemo(
    () => selectedOptions.filter(f => !f.isSecondary === !isSecondary && !f.isDeleted),
    [selectedOptions, isSecondary]
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTxt(e.target.value);
    setIsInitialized(false);
  };
  const handleSaveClick = () => {
    setSelectedFinFields(selectedOptions, excludedOptions, includedOptions);
    handleCloseDialog();
  };

  const toggleExpand = (type: DataType) => {
    const newState = !typeExpanded[type];
    setTypeExpanded({ ...typeExpanded, [type]: newState });
    // In order to collapse, we also need to collapse the expanded parent
    if (!newState) {
      setTypeExpandedAll({ ...typeExpandedAll, [type]: newState });
    }
  };

  const toggleExpandAll = (type: DataType) => {
    const newState = !typeExpandedAll[type];
    setTypeExpandedAll({ ...typeExpandedAll, [type]: newState });
    setTypeExpanded({ ...typeExpanded, [type]: newState });
  };

  const saveDisabled = useMemo(() => {
    // checking if there is any difference between card selected fields and user selection
    if (
      selectedOptions.length !== selectedFinFields.length ||
      selectedOptions.some((option, i) => selectedFinFields[i].ref !== option.ref)
    ) {
      return false;
    }
    const options = getFinFieldsOptions();
    const excluded: Record<string, string[]> = {};
    const included: Record<string, string[]> = {};
    options.forEach(o => {
      excluded[o.ref] = o.options?.excludedChildren || [];
      included[o.ref] = o.options?.includedChildren || [];
    });

    if (
      Object.keys(excluded).length !== Object.keys(excludedOptions).length ||
      Object.keys(included).length !== Object.keys(includedOptions).length
    ) {
      return false;
    }
    let isDifferent = false;
    let keys = Object.keys(excluded);
    for (const ref of keys) {
      if (
        excluded[ref].length !== excludedOptions[ref].length ||
        excluded[ref].find(e => !excludedOptions[ref].includes(e))
      ) {
        isDifferent = true;
        break;
      }
    }
    if (isDifferent) {
      return false;
    }
    keys = Object.keys(included);
    for (const ref of keys) {
      if (
        included[ref].length !== includedOptions[ref].length ||
        included[ref].find(e => !includedOptions[ref].includes(e))
      ) {
        isDifferent = true;
        break;
      }
    }
    return !isDifferent;
  }, [selectedFinFields, selectedOptions, excludedOptions, includedOptions, getFinFieldsOptions]);

  const onSelectOption = (ref: string) => {
    const field = allFinFieldsArray.find(f => f.ref === ref) || rowFormulasArray.find(f => f.ref === ref);
    if (field) {
      setSelectedOptions([...selectedOptions, { ...field, isSecondary }]);
    }
  };

  const onUnselectOption = (ref: string) => {
    setSelectedOptions(selectedOptions.filter(f => f.ref !== ref));
  };

  const flattenedPL = useMemo(() => (allFinFields[plIndex] ? flattenFinFields([allFinFields[plIndex]]) : []), [
    allFinFields,
    plIndex
  ]);
  const flattenedBS = useMemo(() => (allFinFields[bsIndex] ? flattenFinFields([allFinFields[bsIndex]]) : []), [
    allFinFields,
    bsIndex
  ]);
  const flattenedCF = useMemo(() => (allFinFields[cfIndex] ? flattenFinFields([allFinFields[cfIndex]]) : []), [
    allFinFields,
    cfIndex
  ]);
  const flattenedDrivers = useMemo(() => (allFinFields[drIndex] ? flattenFinFields([allFinFields[drIndex]]) : []), [
    allFinFields,
    drIndex
  ]);

  useEffect(() => {
    if (!isInitialized) {
      if (searchTxt) {
        setTypeExpanded({ finData: true, drivers: true, formulas: true });
        setTypeExpandedAll({ finData: true, drivers: true, formulas: true });
      } else {
        setTypeExpanded({ finData: false, drivers: false, formulas: false });
        setTypeExpandedAll({ finData: false, drivers: false, formulas: false });
      }
      setIsInitialized(true);
    } else {
      const filteredPL = searchTxt
        ? {
            ...allFinFields[plIndex],
            children: flattenedPL.filter(field => field.name.toLowerCase().includes(searchTxt.toLowerCase()))
          }
        : allFinFields[plIndex];
      const filteredBS = searchTxt
        ? {
            ...allFinFields[bsIndex],
            children: flattenedBS.filter(field => field.name.toLowerCase().includes(searchTxt.toLowerCase()))
          }
        : allFinFields[bsIndex];
      const filteredCF = searchTxt
        ? {
            ...allFinFields[cfIndex],
            children: flattenedCF.filter(field => field.name.toLowerCase().includes(searchTxt.toLowerCase()))
          }
        : allFinFields[cfIndex];
      const finData = [];
      if ((filteredPL?.children || []).length > 0) {
        finData.push({
          option: filteredPL,
          key: 'PAndL-list',
          overrideName: intl.formatMessage({
            id: 'reporting.fin_statement_profit_loss',
            defaultMessage: 'Financial Statement: Profit & Loss'
          }),
          description: intl.formatMessage({
            id: 'reporting.adds_categories_and_formulas',
            defaultMessage: '(adds all categories and formulas)'
          }),
          isParent: true
        });
      }
      if ((filteredBS?.children || []).length > 0) {
        finData.push({
          option: filteredBS,
          key: 'BS-list',
          overrideName: intl.formatMessage({
            id: 'reporting.fin_statement_balance_sheet_note',
            defaultMessage: 'Financial Statement: Balance Sheet'
          }),
          description: intl.formatMessage({
            id: 'reporting.adds_categories_and_formulas',
            defaultMessage: '(adds all categories and formulas)'
          }),
          isParent: true
        });
      }
      if ((filteredCF?.children || []).length > 0) {
        finData.push({
          option: filteredCF,
          key: 'CF-list',
          overrideName: intl.formatMessage({
            id: 'reporting.fin_statement_cashflow_note',
            defaultMessage: 'Financial Statement: Cash Flow Statement'
          }),
          description: intl.formatMessage({
            id: 'reporting.adds_categories_and_formulas',
            defaultMessage: '(adds all categories and formulas)'
          }),
          isParent: true
        });
      }
      setDisplayedData({
        finData,
        drivers: (searchTxt
          ? flattenedDrivers.filter(f => f.name.toLowerCase().includes(searchTxt.toLowerCase()))
          : allFinFields[drIndex]?.children || []
        ).map(driver => ({ option: driver, key: `${driver.ref}-list` })),
        formulas: (searchTxt
          ? rowFormulasArray.filter(rf => rf.name.toLowerCase().includes(searchTxt.toLowerCase()))
          : rowFormulas
        ).map(formula => ({ option: formula, key: `${formula.ref}-list` }))
      });
    }
  }, [
    allFinFields,
    intl,
    rowFormulas,
    searchTxt,
    rowFormulasArray,
    flattenedPL,
    flattenedBS,
    flattenedCF,
    flattenedDrivers,
    plIndex,
    bsIndex,
    cfIndex,
    drIndex,
    isInitialized
  ]);

  useEffect(() => {
    const formulas = getTransformedRowFormulas(rowFormulaData || []);
    if (isFutrliTemplate) {
      setRowFormulas(formulas);
      setRowFormulasArray(getFlattenedFormulas(formulas));
    } else {
      const templateFormulas = getTransformedRowFormulas(
        (futrliFormulaData || []).filter(f => f.status === 'published'),
        'template-formula',
        true
      );
      let rowFormulas: DataField[] = formulas;
      if (templateFormulas.length) {
        rowFormulas = [
          {
            name: intl.formatMessage({ id: 'reporting.formula_library', defaultMessage: 'Formula library' }),
            ref: 'futrli',
            children: templateFormulas,
            classifier: 'template-formula',
            disabled: false,
            locked: true
          }
        ];
        if (formulas.length) {
          rowFormulas.push({
            name: intl.formatMessage({
              id: 'reporting.organisation_formulas',
              defaultMessage: 'Organisation formulas'
            }),
            ref: 'organisation',
            children: formulas,
            classifier: 'row-formula',
            disabled: false
          });
        }
      }

      setRowFormulas(rowFormulas);
      setRowFormulasArray(getFlattenedFormulas(rowFormulas));
    }
  }, [rowFormulaData, futrliFormulaData, isFutrliTemplate, intl, getTransformedRowFormulas]);

  const highlightField = (ref: string) => {
    setTimeout(() => {
      setSearchTxt('');
      setTypeExpandedAll({ finData: true, drivers: true, formulas: true });
      setHighlightedRef(ref);
      const element = document.getElementById(`option-${ref}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
    setTimeout(() => {
      setHighlightedRef('');
    }, 10000);
  };

  const handleExcludeFinField = (parentRef: string, ref: string) => {
    if (includedOptions[parentRef]?.includes(ref)) {
      setIncludedOptions({ ...includedOptions, [parentRef]: includedOptions[parentRef].filter(r => r !== ref) });
    } else if (!excludedOptions[parentRef]?.includes(ref)) {
      setExcludedOptions({ ...excludedOptions, [parentRef]: [...(excludedOptions[parentRef] || []), ref] });
    }
  };

  const handleIncludeFinField = (parentRef: string, ref: string) => {
    if (excludedOptions[parentRef]?.includes(ref)) {
      setExcludedOptions({ ...excludedOptions, [parentRef]: excludedOptions[parentRef].filter(r => r !== ref) });
      // we need to remove any included children if their parent is not excluded anymore
      const initialLength = includedOptions[parentRef]?.length || 0;
      if (initialLength) {
        const fwc = allFinFieldsArray.find(f => f.ref === ref) || rowFormulasArray.find(f => f.ref === ref);
        if (fwc) {
          includedOptions[parentRef] = includedOptions[parentRef].filter(r => !isChild(fwc, r));
        }
        if (includedOptions[parentRef].length !== initialLength) {
          setIncludedOptions({ ...includedOptions, [parentRef]: includedOptions[parentRef] });
        }
      }
    } else if (!includedOptions[parentRef]?.includes(ref)) {
      setIncludedOptions({ ...includedOptions, [parentRef]: [...(includedOptions[parentRef] || []), ref] });
    }
  };

  const renderDataType = (
    type: DataType,
    name: ReactNode,
    optionsList: DisplayedData[],
    optionType: 'financial' | 'formula' = 'financial'
  ) => {
    return optionsList.length ? (
      <>
        <h4 className={clsx(styles.fieldType, typeExpanded[type] && styles.expanded)}>
          <span className={clsx(styles.fieldTypeText)} onClick={() => toggleExpand(type)} id={`${type}-section`}>
            {name}
          </span>
          <span className={styles.expandControl} onClick={() => toggleExpandAll(type)} id={`${type}-expand-toggle`}>
            {typeExpandedAll[type] ? (
              <FormattedMessage id="common.collapse_all" defaultMessage="Collapse all" />
            ) : (
              <FormattedMessage id="common.expand_all" defaultMessage="Expand all" />
            )}
          </span>
        </h4>
        {(typeExpanded[type] || typeExpandedAll[type]) && (
          <ul className={styles.dataGroup}>
            {optionsList.map(optionList => (
              <DataOption
                key={optionList.key}
                option={optionList.option}
                type={optionType}
                selectedFields={filteredSelectedOptions}
                onSelectOption={onSelectOption}
                onUnselectOption={onUnselectOption}
                overrideName={optionList.overrideName}
                description={optionList.description}
                defaultExpandedState={typeExpandedAll[type]}
                openFormulaDialog={openFormulaDialog}
                hideChildren={searchTxt !== ''}
                isParent={optionList.isParent}
                highlightedRef={highlightedRef}
                selectedParentRef={optionList.option.ref}
              />
            ))}
          </ul>
        )}
      </>
    ) : null;
  };

  return (
    <ReportingSidebar
      aria-label={intl.formatMessage({
        id: 'reporting.choose_or_edit_data_for_widget',
        defaultMessage: 'Choose or edit data to be included in your chart, table or scorecard'
      })}
      title={intl.formatMessage({
        id: 'reporting.choose_or_edit_data_for_widget',
        defaultMessage: 'Choose or edit data to be included in your chart, table or scorecard'
      })}
      open={!!isOpenDialog}
      onCancel={handleCloseDialog}
      sidebarProps={{
        width: '85%'
      }}
    >
      <Form
        onSubmit={e => e.preventDefault()}
        rightSideButtons={
          <div>
            <Button size="small" mr={2} buttonType="secondary" onClick={handleCloseDialog}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            <Button size="small" buttonType="primary" disabled={saveDisabled} onClick={handleSaveClick}>
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
          </div>
        }
        stickyFooter
      >
        <div className={clsx(styles.dialogContent, styles.dialogContentCarbon)}>
          <p className={styles.info}>
            <FormattedMessage
              id="reporting.select_data_modal_info"
              defaultMessage="To enable flexible reporting: choosing a data parent will automatically include the children. Children can be also selected independently."
            />
          </p>
          <div className={styles.boxesContainer}>
            <div className={styles.box}>
              <div className={styles.search}>
                <input
                  className={styles.searchInput}
                  type="text"
                  placeholder={intl.formatMessage({
                    id: 'reporting.search_selector_placeholder',
                    defaultMessage: 'Search for financial, driver and formula data...'
                  })}
                  value={searchTxt}
                  onChange={handleSearch}
                />
                <div className={styles.searchIconContainer}>
                  <Icon type="search" className={styles.searchIconCarbon} color="#fff" />
                  <FormattedMessage id="common.search" defaultMessage="Search" />
                </div>
              </div>

              {/* Financial data */}
              {renderDataType(
                'finData',
                <FormattedMessage id="reporting.financial_data" defaultMessage="Financial data" />,
                displayedData.finData,
                'financial'
              )}

              {/* Formulas */}
              {renderDataType(
                'formulas',
                <FormattedMessage id="reporting.formulas" defaultMessage="Formulas" />,
                displayedData.formulas,
                'formula'
              )}

              {/* Drivers */}
              {allFinFields[drIndex]?.children?.length
                ? renderDataType(
                    'drivers',
                    <FormattedMessage id="reporting.grouping.drivers" defaultMessage="Drivers" />,
                    displayedData.drivers,
                    'financial'
                  )
                : null}
            </div>
            <div className={clsx(styles.box, styles.rightBox)}>
              <h4>
                <FormattedMessage id="reporting.all_data_added" defaultMessage="All data added" />
              </h4>
              <p className={styles.description}>
                <FormattedMessage
                  id="reporting.selection_info_description"
                  defaultMessage="You will choose whether to display parents, children or both when rows have been saved in the builder."
                />
              </p>
              <ul>
                {filteredSelectedOptions.map(option => (
                  <DataOption
                    key={option.ref}
                    option={option}
                    overrideName={
                      option.ref !== 'cash_flow' && flattenedCF.find(f => f.ref === option.ref)
                        ? `${option.name} - ${intl.formatMessage({
                            id: 'financial_fields.type.cash_flow',
                            defaultMessage: 'Cash Flow'
                          })}`
                        : option.name
                    }
                    type="financial"
                    selectedFields={filteredSelectedOptions}
                    onUnselectOption={onUnselectOption}
                    highlightField={highlightField}
                    hideCheckbox
                    showDeleteIcon
                    selectedParentRef={option.ref}
                    addToIncludedField={handleIncludeFinField}
                    addToExcludedField={handleExcludeFinField}
                    excludedChildren={excludedOptions[option.ref] || []}
                    includedChildren={includedOptions[option.ref] || []}
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Form>
    </ReportingSidebar>
  );
};
